<script setup lang="ts">
  import { ref, computed, onMounted, onUnmounted, nextTick, watch } from 'vue'
  import { Search, X } from 'lucide-vue-next'
  import { SavedResponsesService } from '@/types/generated/services.gen'
  import type { TSavedResponse, CustomerCase } from '@/types/generated/types.gen'
  import { useTranslation } from '@/composables/useTranslation'
  import { useMetrics } from '@/composables/useMetrics'
  import { EventNameEnum } from '@/types/generated/types.gen'
  import MarkdownIt from 'markdown-it'
  import DOMPurify from 'dompurify'
  import { stripMarkdownFormatting } from '@/lib/textUtils'

  const props = defineProps<{
    placeholderLabel: string
    caseData: CustomerCase | null
  }>()

  const emit = defineEmits<{
    'select-data': [value: string]
  }>()

  const { t } = useTranslation({
    en: {
      selectDataToReplace: 'Select data to replace the field value',
      someAiExtractedData: 'Some AI-extracted data is not displayed in the interface',
      snippets: 'Snippets',
      search: 'Search',
      showMore: 'show more',
      noSnippetsAvailable: 'No snippets available',
      loadingSnippets: 'Loading snippets...'
    },
    de: {
      selectDataToReplace: 'Daten auswählen, um den Feldwert zu ersetzen',
      someAiExtractedData:
        'Einige KI-extrahierte Daten werden nicht in der Benutzeroberfläche angezeigt',
      snippets: 'Snippets',
      search: 'Suchen',
      showMore: 'mehr anzeigen',
      noSnippetsAvailable: 'Keine Snippets verfügbar',
      loadingSnippets: 'Snippets werden geladen...'
    }
  })

  const isPopupOpen = ref(false)
  const searchQuery = ref('')
  const snippets = ref<TSavedResponse[]>([])
  const isLoading = ref(false)
  const expandedSnippets = ref<Set<string>>(new Set())
  const { trackEvent } = useMetrics()

  const md = new MarkdownIt({
    html: false, // Disable raw HTML for security
    breaks: true,
    linkify: true
  })

  /**
   * Sanitize HTML content to prevent XSS attacks
   * @param html - Raw HTML string
   * @returns Sanitized HTML safe for rendering
   */
  const sanitizeHtml = (html: string): string => {
    return DOMPurify.sanitize(html, {
      ALLOWED_TAGS: [
        'table',
        'thead',
        'tbody',
        'tr',
        'th',
        'td',
        'p',
        'br',
        'strong',
        'em',
        'u',
        'a'
      ],
      ALLOWED_ATTR: ['style', 'class', 'href'],
      ALLOW_DATA_ATTR: false
    })
  }

  const popupRef = ref<HTMLDivElement | null>(null)
  const buttonRef = ref<HTMLButtonElement | null>(null)
  const showAbove = ref(false)
  const scrollTick = ref(0)
  const scrollX = ref(0)
  const scrollY = ref(0)
  const popupLeft = ref(0)
  const popupTop = ref(0)

  const POPUP_OPEN_EVENT = 'jupus-data-replacement-open'
  const POPUP_MARGIN = 16
  const VERTICAL_PLACEMENT_THRESHOLD = 0.6
  const instanceId = crypto.randomUUID()
  const eventController = new AbortController()

  const filteredSnippets = computed(() => {
    if (!searchQuery.value) return snippets.value

    const query = searchQuery.value.toLowerCase()
    return snippets.value.filter((snippet) => {
      const title = snippet.title?.toLowerCase() ?? ''
      const content = snippet.content?.toLowerCase() ?? ''
      return title.includes(query) || content.includes(query)
    })
  })

  const shouldUseHtml = (content: string): boolean => {
    return content.length >= 50 || isTableContent(content)
  }

  const truncateText = (text: string, maxLength: number = 350): string => {
    if (text.length <= maxLength) return text
    return text.substring(0, maxLength) + '...'
  }

  const truncateTable = (content: string): string => {
    if (!isTableContent(content)) return content

    const lines = content.split('\n').filter((line) => line.trim())
    if (lines.length <= 4) return content // Show full table if 4 or fewer lines (header + separator + up to 2 rows)

    // Find table structure
    const markdownTable = convertToMarkdownTable(content)
    const tableLines = markdownTable.split('\n').filter((line) => line.trim())

    // Find separator line (contains ---)
    const separatorIndex = tableLines.findIndex((line) => line.includes('---'))
    if (separatorIndex === -1) return content

    // Take header, separator, and first 3 data rows
    const truncatedLines = tableLines.slice(0, separatorIndex + 4) // header + separator + 3 rows
    return truncatedLines.join('\n')
  }

  const isTextLong = (text: string, maxLength: number = 200): boolean => {
    return text.length > maxLength
  }

  const isTableLong = (content: string): boolean => {
    if (!isTableContent(content)) return false

    const markdownTable = convertToMarkdownTable(content)
    const tableLines = markdownTable.split('\n').filter((line) => line.trim())

    // Find separator line (contains ---)
    const separatorIndex = tableLines.findIndex((line) => line.includes('---'))
    if (separatorIndex === -1) return false

    // Check if there are more than 3 data rows after separator
    const dataRows = tableLines.slice(separatorIndex + 1)
    return dataRows.length > 3
  }

  const shouldShowFullTable = (content: string): boolean => {
    // For tables, show full table only if expanded
    return false
  }

  const toggleExpanded = (snippetId: string) => {
    if (expandedSnippets.value.has(snippetId)) {
      expandedSnippets.value.delete(snippetId)
    } else {
      expandedSnippets.value.add(snippetId)
    }
  }

  const isExpanded = (snippetId: string): boolean => {
    return expandedSnippets.value.has(snippetId)
  }

  const convertToMarkdownTable = (content: string): string => {
    const lines = content.split('\n').filter((line) => line.trim())
    if (lines.length < 2) return content

    const hasTableStructure = lines.some((line) => line.includes('|') || line.includes('\t'))
    if (!hasTableStructure) return content

    // Find actual table rows and handle mixed description+header lines
    const processedLines: { type: 'description' | 'table'; content: string; cells?: string[] }[] =
      []

    lines.forEach((line, index) => {
      if (line.includes('|') || line.includes('\t')) {
        // Split the line by separators and strip markdown from each cell
        const parts = line.split(/\s*\|\s*|\t+/).map((part) => stripMarkdownFormatting(part.trim()))

        // Check if the first part looks like descriptive text (long text ending with colon)
        if (parts.length >= 3 && parts[0].length > 30 && parts[0].endsWith(':')) {
          // This is likely description + table headers on same line
          processedLines.push({ type: 'description', content: parts[0] })

          // The remaining parts are likely table headers
          const headerCells = parts.slice(1).filter((cell) => cell && !cell.match(/^-+$/))
          if (headerCells.length >= 2) {
            processedLines.push({
              type: 'table',
              content: `| ${headerCells.join(' | ')} |`,
              cells: headerCells
            })
          }
        } else {
          // Regular table row
          const cells = parts.filter((cell) => cell && !cell.match(/^-+$/))
          if (cells.length >= 2) {
            processedLines.push({ type: 'table', content: `| ${cells.join(' | ')} |`, cells })
          }
        }
      } else {
        // Regular description line
        processedLines.push({ type: 'description', content: stripMarkdownFormatting(line) })
      }
    })

    // Find consistent table structure
    const tableLines = processedLines.filter((line) => line.type === 'table')
    if (tableLines.length < 2) return content

    // Find consistent column count
    let consistentColumnCount = -1
    let tableStartIndex = -1

    for (let i = 0; i < tableLines.length - 1; i++) {
      const currentCells = tableLines[i].cells?.length || 0
      const nextCells = tableLines[i + 1].cells?.length || 0

      if (currentCells === nextCells && currentCells >= 2) {
        // Check for more consecutive rows with same column count
        let consecutiveRows = 2
        for (let j = i + 2; j < tableLines.length; j++) {
          if ((tableLines[j].cells?.length || 0) === currentCells) {
            consecutiveRows++
          } else {
            break
          }
        }

        if (consecutiveRows >= 2) {
          consistentColumnCount = currentCells
          tableStartIndex = i
          break
        }
      }
    }

    if (tableStartIndex === -1 || consistentColumnCount === -1) return content

    // Separate description from table
    const descriptionLines: string[] = []
    const validTableLines: string[] = []

    // Add all description lines that come before the table
    let foundTableStart = false
    for (const line of processedLines) {
      if (line.type === 'table' && (line.cells?.length || 0) === consistentColumnCount) {
        foundTableStart = true
        validTableLines.push(line.content)
      } else if (!foundTableStart) {
        descriptionLines.push(line.content)
      }
    }

    if (validTableLines.length >= 2) {
      // Add markdown table separator after first row
      const separator = `|${' --- |'.repeat(consistentColumnCount)}`
      const markdownTable = [validTableLines[0], separator, ...validTableLines.slice(1)].join('\n')

      // Combine description with table
      if (descriptionLines.length > 0) {
        return descriptionLines.join('\n') + '\n\n' + markdownTable
      }

      return markdownTable
    }

    return content
  }

  const isTableContent = (content: string): boolean => {
    const lines = content.split('\n').filter((line) => line.trim())
    return lines.length > 2 && lines.some((line) => line.includes('|') || line.includes('\t'))
  }

  const renderSnippetContent = (content: string): string => {
    // Strip markdown formatting first
    const cleanContent = stripMarkdownFormatting(content)

    if (isTableContent(cleanContent)) {
      const markdownTable = convertToMarkdownTable(cleanContent)
      const rendered = md.render(markdownTable)
      return sanitizeHtml(rendered)
    }

    // For very short content, don't use markdown to avoid extra paragraph wrapping
    if (cleanContent.length < 100) {
      return sanitizeHtml(cleanContent)
    }

    const rendered = md.render(cleanContent)
    return sanitizeHtml(rendered)
  }

  const loadSnippets = async () => {
    isLoading.value = true
    try {
      const response = await SavedResponsesService.savedResponsesList()
      snippets.value = (response.results || []).sort(
        (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      )
    } catch (error) {
      console.error('Error loading snippets:', error)
      snippets.value = []
    } finally {
      isLoading.value = false
    }
  }

  const handleGlobalOpen = (evt: Event) => {
    const customEvent = evt as CustomEvent<{ instanceId: string }>
    if (customEvent.detail?.instanceId !== instanceId && isPopupOpen.value) {
      closePopup()
    }
  }

  const determinePosition = () => {
    if (!buttonRef.value || !isPopupOpen.value) return

    const buttonRect = buttonRef.value.getBoundingClientRect()
    const viewportHeight = window.innerHeight
    const viewportWidth = window.innerWidth

    showAbove.value = buttonRect.top > viewportHeight * VERTICAL_PLACEMENT_THRESHOLD

    const popupWidth = popupRef.value?.offsetWidth || 0
    const popupHeight = popupRef.value?.offsetHeight || 0

    let desiredTop = showAbove.value
      ? buttonRect.top + window.scrollY - POPUP_MARGIN - popupHeight
      : buttonRect.bottom + window.scrollY + POPUP_MARGIN

    popupTop.value = desiredTop

    let desiredLeft = buttonRect.right + window.scrollX

    const maxLeft = window.scrollX + viewportWidth - popupWidth - POPUP_MARGIN
    if (desiredLeft > maxLeft) {
      const leftOfButton = buttonRect.left + window.scrollX - popupWidth
      desiredLeft = Math.max(window.scrollX + POPUP_MARGIN, leftOfButton)
    }

    const minLeft = window.scrollX + POPUP_MARGIN
    popupLeft.value = Math.min(Math.max(desiredLeft, minLeft), Math.max(minLeft, maxLeft))
  }

  const handleButtonClick = (event: MouseEvent) => {
    event.stopPropagation()
    isPopupOpen.value = true
    window.dispatchEvent(new CustomEvent(POPUP_OPEN_EVENT, { detail: { instanceId } }))
    // Track snippet use in document generator
    trackEvent(EventNameEnum.SNIPPET_BUTTON_CLICKED_IN_DOCUMENT_GENERATOR, {})
    if (snippets.value.length === 0) {
      loadSnippets()
    }

    nextTick(() => {
      updateOffsetsAndPosition({ bumpTick: true })
    })
  }

  const closePopup = () => {
    isPopupOpen.value = false
    searchQuery.value = ''
    expandedSnippets.value.clear()
  }

  const selectSnippet = async (snippet: TSavedResponse) => {
    // Track snippet used in document generator event
    trackEvent(EventNameEnum.SNIPPET_SELECTED_IN_DOCUMENT_GENERATOR, {
      snippet_uuid: snippet.uuid,
      snippet_title: snippet.title,
      session_type: snippet.session_type,
      content_length: snippet.content?.length || 0
    })

    // Strip markdown formatting before emitting
    const cleanContent = stripMarkdownFormatting(snippet.content || '')
    emit('select-data', cleanContent)
    closePopup()
  }

  const handleClickOutside = (event: MouseEvent) => {
    if (
      isPopupOpen.value &&
      popupRef.value &&
      buttonRef.value &&
      !popupRef.value.contains(event.target as Node) &&
      !buttonRef.value.contains(event.target as Node)
    ) {
      closePopup()
    }
  }

  const updateOffsetsAndPosition = (options?: { bumpTick?: boolean }) => {
    scrollX.value = window.scrollX
    scrollY.value = window.scrollY
    if (options?.bumpTick) {
      scrollTick.value++
    }
    determinePosition()
  }

  const handleScroll = () => {
    if (isPopupOpen.value) {
      updateOffsetsAndPosition({ bumpTick: true })
    }
  }

  const handleResize = () => {
    if (isPopupOpen.value) {
      updateOffsetsAndPosition()
    }
  }

  onMounted(() => {
    document.addEventListener('click', handleClickOutside, { signal: eventController.signal })
    window.addEventListener('scroll', handleScroll, {
      passive: true,
      signal: eventController.signal
    })
    window.addEventListener('resize', handleResize, { signal: eventController.signal })
    window.addEventListener(POPUP_OPEN_EVENT, handleGlobalOpen, {
      signal: eventController.signal
    })
    updateOffsetsAndPosition()
  })

  onUnmounted(() => {
    eventController.abort()
  })

  // Watch for case data changes to reload snippets
  watch(
    () => props.caseData?.uuid,
    () => {
      if (isPopupOpen.value) {
        loadSnippets()
      }
    }
  )
</script>

<template>
  <div class="relative inline-flex">
    <button
      ref="buttonRef"
      type="button"
      class="flex h-8 w-8 items-center justify-center rounded-md border border-gray-300 bg-white text-gray-400
        hover:bg-gray-50 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-purple-500
        focus:ring-offset-2"
      @click="handleButtonClick"
    >
      <svg
        class="h-4 w-4"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
        />
      </svg>
    </button>

    <Teleport to="body">
      <Transition
        enter-active-class="transition duration-200 ease-out"
        enter-from-class="transform scale-95 opacity-0"
        enter-to-class="transform scale-100 opacity-100"
        leave-active-class="transition duration-75 ease-in"
        leave-from-class="transform scale-100 opacity-100"
        leave-to-class="transform scale-95 opacity-0"
      >
        <div
          v-if="isPopupOpen"
          ref="popupRef"
          :style="{
            position: 'absolute',
            left: popupLeft + 'px',
            top: popupTop + 'px',
            '--tick': String(scrollTick)
          }"
          class="z-50 w-[800px] rounded-lg border border-gray-200 bg-white shadow-lg"
        >
          <!-- Header -->
          <div class="border-b border-gray-200 p-4">
            <div class="flex items-start justify-between">
              <div>
                <h3 class="text-lg font-medium text-gray-900">{{ t('selectDataToReplace') }}</h3>
                <p class="mt-1 text-sm text-gray-500">{{ t('someAiExtractedData') }}</p>
              </div>
              <button
                type="button"
                class="ml-4 inline-flex items-center justify-center rounded-md text-gray-400 hover:text-gray-500"
                @click="closePopup"
              >
                <X class="h-5 w-5" />
              </button>
            </div>
          </div>

          <!-- Main content area with sidebar -->
          <div class="flex">
            <!-- Left sidebar with categories -->
            <div class="w-1/3 border-r border-gray-200 p-4">
              <div class="space-y-1">
                <button
                  class="flex w-full items-center justify-between rounded-lg border border-purple-200 bg-purple-50 px-3 py-2
                    text-left text-sm font-medium text-purple-700 transition-colors"
                >
                  <span>{{ t('snippets') }}</span>
                  <span
                    v-if="snippets.length > 0"
                    class="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600"
                  >
                    {{ snippets.length }}
                  </span>
                </button>
              </div>
            </div>

            <!-- Right content area -->
            <div class="w-2/3">
              <!-- Search -->
              <div class="border-b border-gray-200 p-4">
                <div class="relative">
                  <div class="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                    <Search class="h-4 w-4 text-gray-400" />
                  </div>
                  <input
                    v-model="searchQuery"
                    type="text"
                    class="block w-full rounded-lg border-gray-300 pl-10 focus:border-purple-500 focus:ring-purple-500"
                    :placeholder="t('search')"
                  />
                </div>
              </div>

              <!-- Content -->
              <div class="max-h-[400px] overflow-y-auto p-4">
                <!-- Loading state -->
                <div
                  v-if="isLoading"
                  class="py-8 text-center text-gray-500"
                >
                  {{ t('loadingSnippets') }}
                </div>

                <!-- No snippets -->
                <div
                  v-else-if="filteredSnippets.length === 0"
                  class="py-8 text-center text-gray-500"
                >
                  {{ t('noSnippetsAvailable') }}
                </div>

                <!-- Snippets list -->
                <div
                  v-else
                  class="space-y-3"
                >
                  <div
                    v-for="snippet in filteredSnippets"
                    :key="snippet.uuid"
                    :class="[
                      'cursor-pointer rounded-lg border border-gray-200 transition-colors hover:bg-gray-50',
                      isTextLong(snippet.content || '') || isTableLong(snippet.content || '')
                        ? 'p-4'
                        : 'p-3'
                    ]"
                    @click="selectSnippet(snippet)"
                  >
                    <div class="flex items-start justify-between">
                      <div class="min-w-0 flex-1">
                        <h4 class="truncate font-medium text-gray-900">
                          {{ snippet.title || 'Untitled Snippet' }}
                        </h4>
                        <div
                          :class="
                            isTextLong(snippet.content || '') || isTableLong(snippet.content || '')
                              ? 'mt-2'
                              : 'mt-1'
                          "
                        >
                          <div
                            v-if="
                              !isExpanded(snippet.uuid) &&
                              (isTextLong(snippet.content || '') ||
                                isTableLong(snippet.content || ''))
                            "
                            :class="[
                              isTableContent(snippet.content || '')
                                ? `prose prose-sm max-w-none text-sm prose-table:table-auto prose-table:border-collapse prose-th:border
                                  prose-th:border-gray-300 prose-th:bg-gray-50 prose-th:px-2 prose-th:py-1 prose-td:border
                                  prose-td:border-gray-300 prose-td:px-2 prose-td:py-1`
                                : 'snippet-content whitespace-pre-wrap text-sm text-gray-600'
                            ]"
                          >
                            <span v-if="!shouldUseHtml(snippet.content || '')">
                              {{ stripMarkdownFormatting(truncateText(snippet.content || '')) }}
                            </span>
                            <span
                              v-else-if="isTableContent(snippet.content || '')"
                              v-html="renderSnippetContent(truncateTable(snippet.content || ''))"
                            ></span>
                            <span
                              v-else
                              v-html="renderSnippetContent(truncateText(snippet.content || ''))"
                            ></span>
                          </div>
                          <div
                            v-else
                            :class="[
                              isTableContent(snippet.content || '')
                                ? `prose prose-sm max-w-none text-sm prose-table:table-auto prose-table:border-collapse prose-th:border
                                  prose-th:border-gray-300 prose-th:bg-gray-50 prose-th:px-2 prose-th:py-1 prose-td:border
                                  prose-td:border-gray-300 prose-td:px-2 prose-td:py-1`
                                : 'snippet-content whitespace-pre-wrap text-sm text-gray-600'
                            ]"
                          >
                            <span v-if="!shouldUseHtml(snippet.content || '')">
                              {{ stripMarkdownFormatting(snippet.content || '') }}
                            </span>
                            <span
                              v-else
                              v-html="renderSnippetContent(snippet.content || '')"
                            ></span>
                          </div>

                          <button
                            v-if="
                              isTextLong(snippet.content || '') ||
                              isTableLong(snippet.content || '')
                            "
                            class="-mt-3 block text-sm text-purple-600 underline hover:text-purple-800"
                            @click.stop="toggleExpanded(snippet.uuid)"
                          >
                            {{ isExpanded(snippet.uuid) ? 'show less' : t('showMore') }}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<style>
  .snippet-content p {
    margin: 0 !important;
    padding: 0 !important;
  }

  .snippet-content * {
    margin: 0 !important;
    padding: 0 !important;
  }

  /* More specific selector to ensure it works */
  div.snippet-content p {
    margin: 0 !important;
    padding: 0 !important;
  }
</style>
